import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

import useBackendApi from '../../../../../hooks/useBackendApi';
import type { RequestBody } from '../../../../../types/httpRequests';
import type { AccountEvent } from '../../../../../types/account';
import {
  EventInvoiceDraftResponse,
  EventInvoiceLine,
  EventInvoicePreviewResponse,
  QuickBooksInvoiceState,
  QuickBooksSyncStatus,
  invoiceErrorMessage,
  parseInvoiceDraftResponse,
  parseInvoicePreviewResponse,
} from './eventInvoiceApi';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type EventInvoiceSectionProps = {
  event: AccountEvent;
  orgPathName: string;
  accountPathName: string;
  refreshKey?: number;
};

const invoiceStatusLabel = (
  quickbooksSyncStatus: QuickBooksSyncStatus,
  invoiceState?: QuickBooksInvoiceState | null,
): string => {
  if (invoiceState === 'VOIDED') return 'Voided in QuickBooks';
  if (invoiceState === 'NOT_FOUND') return 'Not found in QuickBooks';
  if (quickbooksSyncStatus === 'SYNCED') return 'Created in QuickBooks';
  if (quickbooksSyncStatus === 'SYNC_FAILED') return 'Sync failed';
  return 'Ready to sync';
};

const invoiceStatusColor = (
  quickbooksSyncStatus: QuickBooksSyncStatus,
  invoiceState?: QuickBooksInvoiceState | null,
): 'success' | 'error' | 'warning' => {
  if (invoiceState === 'VOIDED') return 'warning';
  if (invoiceState === 'NOT_FOUND') return 'error';
  if (quickbooksSyncStatus === 'SYNCED') return 'success';
  if (quickbooksSyncStatus === 'SYNC_FAILED') return 'error';
  return 'warning';
};

const summaryFromDraft = (draft: EventInvoiceDraftResponse): EventInvoicePreviewResponse => ({
  preview: draft.preview,
  invoice: {
    event_invoice_id: draft.event_invoice_id,
    quickbooks_sync_status: draft.quickbooks_sync_status,
    qb_doc_number: draft.qb_doc_number,
    quickbooks_invoice_state: draft.quickbooks_invoice_state,
    error_message: draft.error_message,
  },
});

type CompletedServicesTableProps = {
  ariaLabel: string;
  lines: EventInvoiceLine[];
};

function CompletedServicesTable({ ariaLabel, lines }: CompletedServicesTableProps): JSX.Element {
  return (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflowX: 'auto' }}>
      <Table aria-label={ariaLabel} size="small" sx={{ minWidth: 360 }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'background.default' }}>
            <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Completed
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.service_name}>
              <TableCell>{line.service_name}</TableCell>
              <TableCell align="right">{line.done_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function EventInvoiceSection({
  event,
  orgPathName,
  accountPathName,
  refreshKey = 0,
}: EventInvoiceSectionProps): JSX.Element {
  const { apiFetchRequest } = useBackendApi();
  const [previewResponse, setPreviewResponse] = useState<EventInvoicePreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [notApplicable, setNotApplicable] = useState(false);
  const autoLoadKeyRef = useRef<string | null>(null);

  const invoice = previewResponse?.invoice || null;
  const preview = previewResponse?.preview || null;
  const isCompleted = event.event_completion_status === 'COMPLETED';

  const previewEndpoint = useMemo(() => {
    const searchParams = new URLSearchParams({
      org_path_name: orgPathName,
      account_path_name: accountPathName,
    });
    return `${backendURL}/invoices/event/${encodeURIComponent(String(event.acc_event_id))}/preview?${searchParams}`;
  }, [accountPathName, event.acc_event_id, orgPathName]);

  const loadPreview = useCallback(
    async (
      { showLoading = true, showError = true }: { showLoading?: boolean; showError?: boolean } = {},
    ): Promise<boolean> => {
      if (showLoading) {
        setPreviewLoading(true);
        setDraftError(null);
        setSuccessMessage(null);
      }
      if (showError) setPreviewError(null);

      try {
        const response = await apiFetchRequest({ endpoint: previewEndpoint, method: 'GET' });
        if (!response || response.isError || response.status !== 200) {
          const detail =
            response?.body && typeof response.body.detail === 'string' ? response.body.detail.toLowerCase() : '';
          if (response?.status === 422 && detail.includes('no completed services')) {
            setNotApplicable(true);
            return true;
          }
          const message = invoiceErrorMessage(response?.body, response?.status);
          if (showError) setPreviewError(message);
          return false;
        }

        const parsed = parseInvoicePreviewResponse(response.body);
        if (!parsed) {
          if (showError) setPreviewError('The invoice preview response was invalid. Please try again.');
          return false;
        }

        setNotApplicable(false);
        setPreviewResponse(parsed);
        return true;
      } catch {
        if (showError) setPreviewError('Unable to load the invoice preview. Please try again.');
        return false;
      } finally {
        if (showLoading) setPreviewLoading(false);
      }
    },
    [apiFetchRequest, previewEndpoint],
  );

  useEffect(() => {
    setPreviewResponse(null);
    setPreviewError(null);
    setDraftError(null);
    setSuccessMessage(null);
    setNotApplicable(false);
  }, [event.acc_event_id, event.event_completion_status]);

  useEffect(() => {
    if (!isCompleted) return;
    const autoLoadKey = `${event.acc_event_id}:${orgPathName}:${accountPathName}:${refreshKey}`;
    if (autoLoadKeyRef.current === autoLoadKey) return;
    autoLoadKeyRef.current = autoLoadKey;
    void loadPreview();
  }, [accountPathName, event.acc_event_id, isCompleted, loadPreview, orgPathName, refreshKey]);

  const submitDraft = useCallback(async () => {
    if (draftSubmitting || !isCompleted || notApplicable) return;

    setDraftSubmitting(true);
    setPreviewError(null);
    setDraftError(null);
    setSuccessMessage(null);

    const body: RequestBody = {
      org_path_name: orgPathName,
      account_path_name: accountPathName,
    };
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/invoices/event/${encodeURIComponent(String(event.acc_event_id))}/draft`,
        method: 'POST',
        body,
      });
      if (!response || response.isError || response.status !== 200) {
        const message = invoiceErrorMessage(response?.body, response?.status);
        await loadPreview({ showLoading: false, showError: false });
        setDraftError(message);
        return;
      }

      const parsed = parseInvoiceDraftResponse(response.body);
      if (!parsed) {
        setDraftError('The QuickBooks draft response was invalid. Please refresh the invoice details.');
        return;
      }

      setPreviewResponse(summaryFromDraft(parsed));
      setSuccessMessage(
        parsed.was_existing
          ? 'The frozen invoice was synchronized with QuickBooks successfully.'
          : 'The draft invoice was created in QuickBooks for accounting review.',
      );
    } catch {
      setDraftError('Unable to create the QuickBooks draft. It is safe to retry.');
    } finally {
      setDraftSubmitting(false);
    }
  }, [
    accountPathName,
    apiFetchRequest,
    draftSubmitting,
    event.acc_event_id,
    isCompleted,
    loadPreview,
    notApplicable,
    orgPathName,
  ]);

  const retryable =
    invoice?.quickbooks_sync_status === 'PENDING_SYNC' || invoice?.quickbooks_sync_status === 'SYNC_FAILED';

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
        <Box
          sx={{
            alignItems: { xs: 'flex-start', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
            <ReceiptLongOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Invoice
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isCompleted
                  ? 'Review the automatically generated invoice and its QuickBooks status.'
                  : 'An invoice will be generated when this event is marked complete.'}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {invoice ? (
              <Chip
                color={invoiceStatusColor(invoice.quickbooks_sync_status, invoice.quickbooks_invoice_state)}
                label={invoiceStatusLabel(invoice.quickbooks_sync_status, invoice.quickbooks_invoice_state)}
                size="small"
              />
            ) : null}
            {isCompleted ? (
              <Button
                disabled={previewLoading || draftSubmitting}
                onClick={() => void loadPreview()}
                size="small"
                startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                variant="outlined"
              >
                {previewLoading ? 'Loading…' : 'Refresh'}
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {!isCompleted ? (
          <Alert severity="info">
            Review and finalize the completed-service counts in Event Completion to generate the invoice.
          </Alert>
        ) : null}

        {isCompleted && previewLoading && !preview ? (
          <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 1.5, py: 4 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Preparing invoice preview…
            </Typography>
          </Box>
        ) : null}

        {isCompleted && previewError ? (
          <Alert severity="error" sx={{ mb: preview ? 2 : 0 }}>
            {previewError}
          </Alert>
        ) : null}

        {isCompleted && successMessage ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        ) : null}

        {isCompleted && draftError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {draftError}
          </Alert>
        ) : null}

        {isCompleted && notApplicable ? (
          <Alert severity="info">No invoice was created because all finalized service counts are zero.</Alert>
        ) : null}

        {isCompleted && preview ? (
          <Box>
            {invoice?.quickbooks_sync_status === 'SYNCED' && invoice.quickbooks_invoice_state === 'ACTIVE' ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                QuickBooks invoice {invoice.qb_doc_number ? `#${invoice.qb_doc_number}` : ''} is ready for accounting
                review and has not been sent to the customer.
              </Alert>
            ) : null}
            {invoice?.quickbooks_invoice_state === 'VOIDED' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This invoice was voided in QuickBooks. QuickBooks remains the accounting record.
              </Alert>
            ) : null}
            {invoice?.quickbooks_invoice_state === 'NOT_FOUND' ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                The referenced invoice no longer exists in QuickBooks.
              </Alert>
            ) : null}
            {retryable ? (
              <Alert severity={invoice?.quickbooks_sync_status === 'SYNC_FAILED' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                {invoice?.quickbooks_sync_status === 'SYNC_FAILED'
                  ? invoice.error_message || 'QuickBooks synchronization failed. It is safe to retry.'
                  : 'The invoice is frozen locally and is waiting to be synchronized with QuickBooks.'}
              </Alert>
            ) : null}

            {preview.lines.length ? <CompletedServicesTable ariaLabel="Invoice preview" lines={preview.lines} /> : null}

            {invoice?.quickbooks_sync_status !== 'SYNCED' ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
                <Button
                  disabled={draftSubmitting}
                  onClick={() => void submitDraft()}
                  startIcon={
                    draftSubmitting ? <CircularProgress size={18} color="inherit" /> : <CloudSyncOutlinedIcon />
                  }
                  variant="contained"
                >
                  {draftSubmitting ? 'Retrying…' : 'Retry QuickBooks Sync'}
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : null}

        {isCompleted && !preview && !previewLoading && !notApplicable ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
            <Button
              disabled={draftSubmitting}
              onClick={() => void submitDraft()}
              startIcon={draftSubmitting ? <CircularProgress size={18} color="inherit" /> : <CloudSyncOutlinedIcon />}
              variant="contained"
            >
              {draftSubmitting ? 'Retrying…' : 'Retry QuickBooks Sync'}
            </Button>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}
